import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import MobileFooter from "./mobileFooter";
import DesktopFooter from "./desktopFooter";
import { addOnModalVisibility } from "../../../../store/slices/common/addOn";
import { BUY_NOW } from "../../../../constants/common/common";
import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import { redirectToCheckout } from "../../../../utils/common";
import { useAddonContinueHandler } from "../useAddonContinueHandler";

/**
 * Index component displays the footer section for the addon selection.
 *
 * This component shows price details including the base price of the product and the
 * price of selected addons. It also includes a continue button for proceeding with
 * the selected items. Depending on whether the user is on a mobile or desktop device,
 * it renders different layouts.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered footer component.
 */
const Index = ({ commonCleverTapData }) => {
  const dispatch = useDispatch();

  const continueWithAddons = useAddonContinueHandler({ commonCleverTapData });

  const {
    pdpSelectedAddons,
    isMobile,
    clientCurrentClickedItem,
    selectedCartProduct,
    continueAddon,
    isBuyNowClicked,
    timeSlots,
  } = useSelector(
    (state) => ({
      pdpSelectedAddons: state.pdpAddon.selectedAddons,
      isMobile: state?.appConfig?.isMobile,
      clientCurrentClickedItem: state?.pdp?.clientCurrentClickedItem,
      selectedCartProduct: state?.cart?.selectedCartProduct,
      continueAddon: state.addOn?.continueAddon,
      isBuyNowClicked: state.pdp?.checkBuyNowOrProceedToPay,
      timeSlots: state.shippingDateAndSlotSelection?.selectedDeliveryDetails?.timeSlots,
    }),
    shallowEqual,
  );

  const basePrice = isEmpty(selectedCartProduct)
    ? clientCurrentClickedItem?.productPrice?.price
    : selectedCartProduct?.price;

  useEffect(() => {
    if (continueAddon && isBuyNowClicked === BUY_NOW) {
      redirectToCheckout();
      dispatch(addOnModalVisibility(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueAddon, isBuyNowClicked]);

  const addons = pdpSelectedAddons;

  /** */
  const calculateAddonTotal = () => {
    return addons.reduce((total, addon) => total + addon.price * addon.quantity, 0);
  };

  /** */
  const calculateAddonCount = () => {
    return addons.reduce((count, addon) => count + addon.quantity, 0);
  };
  const addonsTotal = calculateAddonTotal();

  /**
   * Calculates the total price including base price and the selected addons.
   *
   * @returns {number} The total price, which is the sum of the base price and the total
   * cost of selected addons.
   */
  const calculateTotal = () => {
    return basePrice + addonsTotal + (timeSlots?.timeSlots?.shippingPrice || 0);
  };

  const selectedAddonCount = addons.length;
  const totalPrice = calculateTotal();

  /** */
  const onContinueClick = () => {
    continueWithAddons(false);
  };

  if (isMobile) {
    return (
      <MobileFooter
        totalPrice={totalPrice}
        calculateAddonCount={calculateAddonCount}
        selectedAddonCount={selectedAddonCount}
        onContinueClick={onContinueClick}
      />
    );
  }
  return (
    <DesktopFooter
      calculateAddonCount={calculateAddonCount}
      basePrice={basePrice}
      calculateAddonTotal={calculateAddonTotal}
      calculateTotal={calculateTotal}
      onContinueClick={onContinueClick}
    />
  );
};

Index.propTypes = {
  commonCleverTapData: PropTypes.object,
};
export default Index;
